import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EditOutlined, PlusOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import usePermissions from '../../hooks/usePermissions';
import { deletePlanAddon, listPlanAddons, updatePlanAddonStatus } from '../../services/plan-addon';

const { Title } = Typography;
const { useApp } = App;

const PlanAddonList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listPlanAddons({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total,
      }));
    } catch (err) {
      message.error('No se pudo cargar la lista de paquetes.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'edit':
        navigate(`/video-packages/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
          title: willActivate ? '¿Activar Paquete de Videos?' : '¿Desactivar Paquete de Videos?',
          content: `${record.description || 'Paquete de Videos'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updatePlanAddonStatus(record.id, willActivate);
              message.success(willActivate ? 'Paquete de Videos activada' : 'Paquete de Videos desactivada');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar Paquete de Videos?',
          content: `${record.description || 'Paquete de Videos'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deletePlanAddon(record.id);
              message.success('Paquete de Videos eliminado');
              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch (error) {
              message.error('No se pudo eliminar el paquete de videos');
            }
          },
        });
        break;

      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('plan_addons.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar Paquete de Videos',
      },
      can('plan_addons.toggle') && {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      },
      {
        type: 'divider',
      },
      can('plan_addons.delete') && {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar Paquete de Videos',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { title: 'Código', dataIndex: 'code', key: 'code', sorter: true, },
    { title: 'Nombre', dataIndex: 'name', key: 'name', sorter: true, },
    { title: 'Videos', dataIndex: 'extraVideos', key: 'extraVideos', sorter: true, },
    {
      title: 'Precio', dataIndex: 'price', key: 'price', sorter: true,
      render: (value, record) =>
        new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: record.currency || 'MXN',
        }).format(value),
    },
    { title: 'Moneda', dataIndex: 'currency', key: 'currency', sorter: true, },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      align: 'center',
      render: (color) => (
        <Tooltip title={color || 'Sin color'}>
          <div style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: color || 'transparent',
            border: '1px solid #d9d9d9',
            margin: '0 auto'
          }} />
        </Tooltip>
      )
    },
    {
      title: 'Estado', dataIndex: 'isActive', key: 'isActive', sorter: true,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'volcano'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Paquete de Videos
        </Title>
        {can('plan_addons.add') && (<Tooltip title="Agregar Paquete de Videos">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="btn-add"
            aria-label="Agregar Paquete de Videos"
            onClick={() => navigate('/video-packages/add')}
          />
        </Tooltip>)}
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Paquete de Videos"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} paquetes`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default PlanAddonList;
